import {
  ActionButton, appInjector,
  BaseApiService, BaseDataService, BaseDetailComponent,
  BaseEntity,
  DisplayDataResolver,
  FeedbackMessage,
  PaginatedResult
} from "@pl/pl-lib/common";
import {merge, Observable} from "rxjs";
import {State} from "@progress/kendo-data-query";
import {ActivatedRoute, Router} from "@angular/router";
import {RowArgs, RowClassFn, SelectableSettings, SelectionEvent} from "@progress/kendo-angular-grid";
import {UntypedFormGroup} from "@angular/forms";
import {
  EditEventArgs,
  FinishedEditEventArgs,
  ItemEditEventArgs
} from "@pl/pl-lib/data-grids/src/components/data-grid/data-grid.component";
import {PlEntityListComponent} from "./pl-entity-list.component";
import {DataGridColumn, DataGridComponent, FiltersSettings, ISecurityConfig} from "@pl/pl-lib/data-grids";
import {WindowRef, WindowSettings} from "@progress/kendo-angular-dialog";
import {EntityModalSettings} from "@pl/pl-lib/dialogs/src/services/entity-modal/base-entity-modal.service";
import {EntityDialogService, EntityWindowsService} from "@pl/pl-lib/dialogs";
import {TemplateRef} from "@angular/core";
import type { DetailPreviewPopup } from '@pl/pl-lib/data-grids'

export class DataGridConfigurator {
  tableId: string;
}

export class PlEntityListConfigurator extends DataGridConfigurator {
  render: boolean = true;
  dataLoaderType: 'baseApiService' | 'custom';
  dataLoaderService?: BaseApiService<any, any, any>;
  dataLoaderServiceType?: new () => BaseApiService<any, any, any>;
  dataLoaderFunction?: (state: State, forceAllRecords?: boolean) => Observable<PaginatedResult<any>>;
  countLoaderFunction?: (state: State) => Observable<number>;
  loadOnInit: boolean = true;
  paginated: boolean = true;
  initialState: State;
  clientFilterable: boolean = false;
  selectable?: SelectableSettings | boolean;
  grid: PlEntityListComponent<any>;
  filterable: FiltersSettings;
  secureGridHandler?: (grid: DataGridComponent) => Promise<ISecurityConfig>;
  export: boolean;
  exportAllData: boolean = true;
  cellClickHandler: (entity: any, router: Router, route: ActivatedRoute) => void;
  getMessages: (data?: any) => Promise<FeedbackMessage[]>;
  selectionKeyGenerator?: (context: RowArgs) => string;
  editFormGetter?: (dataItem: any, kendoSender: any, triggerDetails: any) => UntypedFormGroup;
  handleAddItemRequest?: (event: EditEventArgs) => void;
  handleDeleteItemRequest?: (event: ItemEditEventArgs) => void;
  handleEditItemRequest?: (event: ItemEditEventArgs) => void;
  handleEditFinished?: (event: FinishedEditEventArgs) => void;
  handleEditCanceled?: (event: FinishedEditEventArgs) => void;
  customActionClicked?: (event: { action: ActionButton, dropdownItemClicked?: any }) => void;
  isDetailExpanded?: (rowArgs: RowArgs) => boolean;
  rowClass?: RowClassFn;
  selectionChangeHandler?: ($event: SelectionEvent) => void;
  protected entityWindowService: EntityWindowsService;
  protected entityDialogService: EntityDialogService;
  actions?: { [p: string]: ActionButton };
  getCustomColumnTemplate: (column: DataGridColumn) => TemplateRef<any> | null;
  detailPreviewConfig: DetailPreviewPopup;

  constructor(
    public displayDataResolver: DisplayDataResolver<any>,
    service?: BaseApiService<any, any, any>,
    dataLoaderFunction?: (state: State) => Observable<PaginatedResult<any>>,
    countLoaderFunction?: (state: State) => Observable<number>
  ) {
    super();
    this.dataLoaderType = service ? 'baseApiService' : 'custom';
    this.dataLoaderService = service;
    this.dataLoaderFunction = dataLoaderFunction;
    this.countLoaderFunction = countLoaderFunction;
    this.initialState = {skip: 0, take: 50};
    this.export = true;
    this.entityWindowService = appInjector().get(EntityWindowsService);
    this.entityDialogService = appInjector().get(EntityDialogService);
  }

  protected setCrudHandlers(detailsComponentType: new (...args: any[]) => BaseDetailComponent<any, any, any, any, any>,
                            addEntity: (i: any) => BaseEntity,
                            service: BaseDataService<any, any, any, any>,
                            createOptions?: WindowSettings & Partial<EntityModalSettings>,
                            editOptions?: WindowSettings & Partial<EntityModalSettings>,
                            initializeComponent?:(c:BaseDetailComponent<any, any, any, any, any>)=>void
  ) {
    this.selectionChangeHandler = this.disableEditActionSelectionChangeHandler.bind(this);
    this.handleAddItemRequest = (i) => this.baseHandleAddItemRequest(i, addEntity.bind(this), detailsComponentType, createOptions,initializeComponent);
    this.handleEditItemRequest = (i) => this.baseHandleEditItemRequest(i, detailsComponentType, editOptions || createOptions,initializeComponent);
    this.handleDeleteItemRequest = (i) => this.baseHandleDeleteItemRequest(i, detailsComponentType, service);
  }

  protected disableEditActionSelectionChangeHandler() {
    if (this.grid?.grid?.baseGrid) {
      this.grid.grid.baseGrid.baseActions['edit'].disabled = this.grid.grid.baseGrid.mySelectionItems.length !== 1;
    }
  }

  protected baseHandleAddItemRequest(i: any, entity: (i:any) => BaseEntity, detailsComponentType: new (...args: any[]) => BaseDetailComponent<any, any, any, any, any>, options?: WindowSettings & Partial<EntityModalSettings>, initializeComponent?:(c:BaseDetailComponent<any, any, any, any, any>)=>void) {
    const d = this.entityWindowService.openFor(entity(i), detailsComponentType, options || {
      width: 1024,
      height: 800
    }) as WindowRef;

    const c = d.content.instance as BaseDetailComponent<any, any, any, any, any>;
    if (initializeComponent) {
      initializeComponent(c);
    }
    c.saved.subscribe((value) => {
      this.grid.fetch();
    });
  }

  protected baseHandleEditItemRequest = (i:any, detailsComponentType: new (...args: any[]) => BaseDetailComponent<any, any, any, any, any>, options?: WindowSettings & Partial<EntityModalSettings>, initializeComponent?:(c:BaseDetailComponent<any, any, any, any, any>)=>void) => {
    const d = this.entityWindowService.openFor(i.sender.mySelectionItems[0], detailsComponentType, options || {
      width: 1124,
      height: 800
    }) as WindowRef;

    const c = d.content.instance as BaseDetailComponent<any, any, any, any, any>;
    if (initializeComponent) {
      initializeComponent(c);
    }
    merge(c.saved, c.deleted).subscribe((value) => {
      this.grid.fetch();
    });
  }
  protected baseHandleDeleteItemRequest = (i:any, detailsComponentType: new (...args: any[]) => BaseDetailComponent<any, any, any, any, any>, service: BaseDataService<any, any, any, any>) => {
    const items = i.sender.mySelectionItems;
    (detailsComponentType as any).handleDelete(items, this.entityDialogService, this.displayDataResolver, service)
      .subscribe((e: any) => {
        console.log(e);
        this.grid.fetch();
      });
  }

}

